import React, { useMemo } from "react";
import classNames from "classnames/dedupe";
import { mergeDeepRight } from "ramda";

import { DeleteIcon, HeartIconEmpty } from "../../../../icons";
import SvgIcon from "../../../Icons/SvgIcon";
import { TPrice } from "../../../DataDisplay";
import SportLoversPrice from "../../../DataDisplay/Price/SportLoversPrice";
import ProductPrice from "../../../DataDisplay/Price/ProductPrice";
import AddToCartButton from "../../../ProductCard/AddToCartButton/AddToCartButton";

export type TShoppingBagItemType = "default" | "mini" | "look";

interface IShoppingBagAdditionInfo {
  sportLoversPrice?: TPrice;
  type?: TShoppingBagItemType;
  prices?: TPrice[];
  readonly?: boolean;
  isRemoveAvailable?: boolean;
  isFavoriteAvailable?: boolean;
  onRemoveItem?(): void;
  onFavourite?(): void;
  handleAddToCart?(): void;
  disabled?: boolean;
  isAddedToCart?: boolean;
}

const ShoppingBagAdditionInfo = ({
  sportLoversPrice,
  type = "default",
  prices,
  readonly,
  isRemoveAvailable = true,
  isFavoriteAvailable = false,
  onRemoveItem,
  onFavourite,
  disabled,
  isAddedToCart = false,
  handleAddToCart,
}: IShoppingBagAdditionInfo) => {
  const shoppingBagPrices = useMemo(
    () =>
      prices?.map((price) => {
        const isLookOrMini = (
          ["look", "mini"] as TShoppingBagItemType[]
        ).includes(type);

        return {
          ...price,
          classes: mergeDeepRight(price, {
            root: classNames(price.classes?.root, {
              "product-price__current--small":
                price.variant === "current" && isLookOrMini,
              "product-price__original--small": isLookOrMini,
            }),
          }),
        };
      }) ?? [],
    [prices, type],
  );

  return (
    <>
      {sportLoversPrice && (
        <SportLoversPrice
          {...sportLoversPrice}
          classes={{
            root: {
              "product-price__sportLovers--small": ["look", "mini"].includes(
                type,
              ),
            },
          }}
        />
      )}

      <div
        className={classNames("product-numbers", {
          "product-numbers__default": type === "default",
        })}
      >
        <ProductPrice
          classes={{
            root: {
              "justify-content-start align-items-start flex-row": [
                "look",
                "mini",
              ].includes(type),
            },
          }}
          prices={shoppingBagPrices}
        />
      </div>
      {!readonly && isRemoveAvailable && (
        <SvgIcon
          data-testid="ShoppingBag__remove-mini"
          icon={DeleteIcon}
          classes={{ root: "product-remove" }}
          isButton
          onClick={onRemoveItem}
        />
      )}
      {isFavoriteAvailable && !["mini", "look"].includes(type) && (
        <SvgIcon
          data-testid="ShoppingBag__favourite-mini"
          icon={HeartIconEmpty}
          classes={{ root: "product-add-to-favorite" }}
          isButton
          onClick={onFavourite}
        />
      )}
      {type === "look" && !disabled && (
        <div className="position-absolute end-0 bottom-0 lh-0">
          <AddToCartButton
            onClick={handleAddToCart}
            isAddedToCart={isAddedToCart}
          />
        </div>
      )}
    </>
  );
};

export default ShoppingBagAdditionInfo;
